import * as turf from '@turf/turf';
import SphericalMercator from '@mapbox/sphericalmercator';
import { TileUrlProvider } from './TileUrlProvider';
import { AuthorizationFeatureCollection } from './AuthorizationFeatureCollection';
import axios from 'axios';
import { ExtentPadder } from './ExtentPadder';
import { CroppedTileFeatureMerger } from './CroppedTileFeatureMerger';
import { IAuthorityErrata } from './model/authority-tiles/IAuthorityErrata';


// NOTE: would like to port this to ap-tiles/tiles-client... but need to think through
// TileUrlProvider since that is an important part of current local DX when iterating on tiles
export class AuthorizationTileProvider {
    private mercator: SphericalMercator;

    static inject = () => [TileUrlProvider, ExtentPadder, CroppedTileFeatureMerger];
    constructor(private tileUrlProvider: TileUrlProvider, private extentPadder: ExtentPadder, private croppedTileFeatureMerger: CroppedTileFeatureMerger) {
        this.mercator = new SphericalMercator({ size: 256 });
    }
    getAuthorizationFeatures = async (feature: any): Promise<AuthorizationFeatureCollection> => {
        const bbox = turf.bbox(feature);
        const paddedExtent = this.extentPadder.pad({
            extent: {
                xmin: bbox[0],
                ymin: bbox[1],
                xmax: bbox[2],
                ymax: bbox[3]
            },
            xMeters: 75,
            yMeters: 75
        });

        const zoom = 10;
        const { minX, minY, maxX, maxY } = this.mercator.xyz([paddedExtent.xmin, paddedExtent.ymin, paddedExtent.xmax, paddedExtent.ymax], zoom);

        const tiles: { x: number, y: number }[] = [];
        for (let x = minX; x <= maxX; x++) {
            for (let y = minY; y <= maxY; y++) {
                tiles.push({ x, y });
            }
        }

        const responses = await Promise.all(tiles.map(async ({ x, y }) => {
            const url = await this.tileUrlProvider.getAirspaceAuthorityTile({ z: zoom, x, y });
            const res = await axios.get(url);
            if (res.status === 200) {
                return res.data;
            } else if ([204, 404].includes(res.status)) {
                return {
                    type: 'FeatureCollection',
                    features: []
                };
            } else {
                throw new Error(`status ${res.status}`);
            }
        }));

        // NOTE: in some cases, a feature may be entirely duplicated across a tile
        //  so ideally we would dedup here... but we don't currently have a good way
        //  of identifing a unique feature across tiles, especially since the features
        //  are mutated for each tile, independently
        //  likely, we are better off doing this deduping in AuthorizationSubdivide
        //  where it needs to be able to handle conflicting answers to an airspace anyway
        //  eg class E2 on top of D means D, it is reasonable to also do class D on top of D
        //  means the first D won (without reasoning about "are they the same"). This would likely
        //  require we refactor like we did in SubdivideUASFMGeometry using something like airSpaceAlreadyAssessedFeature

        const flatFeatures = responses.reduce((features, res) => features.concat(res.features), []);

        const authorization = new AuthorizationFeatureCollection({ features: flatFeatures });

        const { features: croppedFeatures }  = this.croppedTileFeatureMerger.merge(authorization);

        const errataUrls = await this.tileUrlProvider.getAuthorityErrataUrls();
        const errataResponses = await Promise.all(
            errataUrls.map(errataUrl => axios.get(errataUrl)
                .then(res => res.data as IAuthorityErrata)
                .catch((err: any) => {
                    throw new Error(`failed to GET ${errataUrl}. Got ${err?.response?.status}`);
                }))
        );

        const errata: IAuthorityErrata = {
            'airspace-authority': errataResponses[0]['airspace-authority'],
            adds: [],
            changes: [],
            removes: [],
            errataFor: {},
        };
        for (const errataResponse of errataResponses){
            errata.adds = [...errata.adds, ...errataResponse.adds];
            errata.changes = [...errata.changes, ...errataResponse.changes];
            errata.removes = [...errata.removes, ...errataResponse.removes];
            errata.errataFor = {
                ...errata.errataFor,
                ...errataResponse.errataFor
            };
        }

        const globalIdsToRemove = errata.removes.map(f => f.GLOBAL_ID).concat(errata.changes.map(f => f.properties!.GLOBAL_ID));

        const postErrataFeatures = croppedFeatures.filter(f => !globalIdsToRemove.includes(f.properties!.GLOBAL_ID))
            .concat(errata.adds)
            .concat(errata.changes);

        // typing could be improved if we made this.croppedTileFeatureMerger.merge generic
        return new AuthorizationFeatureCollection({ features: postErrataFeatures as any });
    }

}

